import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Package, Heart, LogOut, Download, ChevronRight } from "lucide-react";
import api, { formatPrice, apiError, BACKEND_URL, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

const TABS = [
    { id: "orders", label: "Orders", icon: Package },
    { id: "wishlist", label: "Wishlist", icon: Heart },
    { id: "profile", label: "Profile", icon: User },
];

const STATUS_LABEL = { pending: "Pending", confirmed: "Confirmed", processing: "Processing", shipped: "Shipped", out_for_delivery: "Out for Delivery", delivered: "Delivered", cancelled: "Cancelled" };

export default function Account() {
    const { user, logout, setUser } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("orders");
    const [orders, setOrders] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "" });

    useEffect(() => {
        api.get("/orders").then(({ data }) => setOrders(data.orders)).catch(() => {});
        api.get("/wishlist").then(({ data }) => setWishlist(data.products)).catch(() => {});
    }, []);

    const doLogout = () => { logout(); toast.success("Logged out"); navigate("/"); };

    const saveProfile = async () => {
        try {
            const { data } = await api.put("/auth/profile", profile);
            setUser(data.user);
            toast.success("Profile updated");
        } catch (err) { toast.error(apiError(err)); }
    };

    const invoice = (orderNumber) => {
        const token = localStorage.getItem("elvosh_token");
        fetch(`${BACKEND_URL}/api/orders/${orderNumber}/invoice`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob()).then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `ELVOSH-${orderNumber}.pdf`; a.click();
                URL.revokeObjectURL(url);
            });
    };

    return (
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-14">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <p className="eyebrow mb-2">My Account</p>
                    <h1 className="font-serif text-3xl md:text-4xl tracking-tight">Hello, {user?.name?.split(" ")[0] || "there"}</h1>
                </div>
                <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors"><LogOut className="h-4 w-4" /> Logout</button>
            </div>

            <div className="grid lg:grid-cols-4 gap-10">
                <aside className="lg:col-span-1">
                    <nav className="flex lg:flex-col gap-2">
                        {TABS.map((t) => (
                            <button key={t.id} data-testid={`account-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors ${tab === t.id ? "bg-ink text-cream" : "hover:bg-cream-soft"}`}>
                                <t.icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
                            </button>
                        ))}
                    </nav>
                </aside>

                <div className="lg:col-span-3">
                    {tab === "orders" && (
                        <div className="space-y-4">
                            {orders.length === 0 ? (
                                <p className="text-muted-foreground py-10 text-center">You haven't placed any orders yet.</p>
                            ) : orders.map((o) => (
                                <div key={o.id} className="border border-[#E8DFD5] p-5 bg-white" data-testid={`account-order-${o.order_number}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                        <div>
                                            <p className="font-medium">{o.order_number}</p>
                                            <p className="text-xs text-muted-foreground">{(o.created_at || "").slice(0, 10)}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-xs uppercase tracking-wider px-3 py-1 ${o.payment?.status === "paid" ? "bg-gold/15 text-gold" : "bg-cream-soft"}`}>{STATUS_LABEL[o.status] || o.status}</span>
                                            <span className="font-semibold">{formatPrice(o.total)}</span>
                                        </div>
                                    </div>
                                    <div className="mt-4 space-y-2 border-t border-[#E8DFD5] pt-3">
                                        {o.items.map((it, i) => (
                                            <div key={i} className="flex items-center gap-3 text-sm">
                                                <div className="h-12 w-10 bg-cream-soft overflow-hidden shrink-0">{it.image && <img src={mediaUrl(it.image)} alt="" className="w-full h-full object-cover" />}</div>
                                                <span className="flex-1">{it.name} × {it.qty}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-4 mt-4 text-sm">
                                        <span className="text-muted-foreground">Payment: {o.payment?.status === "paid" ? "Paid" : o.payment?.status === "cod_pending" ? "COD / Pending" : o.payment?.status}</span>
                                        {(o.payment?.status === "paid" || o.payment?.status === "cod_pending") && (
                                            <button data-testid={`invoice-${o.order_number}`} onClick={() => invoice(o.order_number)} className="flex items-center gap-1.5 text-gold hover:underline ml-auto"><Download className="h-3.5 w-3.5" /> Invoice</button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "wishlist" && (
                        wishlist.length === 0 ? <p className="text-muted-foreground py-10 text-center">Your wishlist is empty.</p> :
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">{wishlist.map((p) => <ProductCard key={p.id} product={p} />)}</div>
                    )}

                    {tab === "profile" && (
                        <div className="max-w-md space-y-4">
                            <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Name</label><input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="w-full mt-1.5 bg-white border border-[#E8DFD5] px-3 py-2.5 text-sm outline-none focus:border-gold" /></div>
                            <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label><input value={user?.email} disabled className="w-full mt-1.5 bg-cream-soft border border-[#E8DFD5] px-3 py-2.5 text-sm text-muted-foreground" /></div>
                            <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</label><input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="w-full mt-1.5 bg-white border border-[#E8DFD5] px-3 py-2.5 text-sm outline-none focus:border-gold" /></div>
                            <button data-testid="profile-save" onClick={saveProfile} className="bg-ink text-cream px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-gold transition-colors">Save Changes</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
